import React from 'react';
import { contracts, invoices, currency, formatDate, getClientName } from '../data';
import { Contract, Invoice } from '../types';

type DetailTarget = { type: 'contract' | 'invoice'; id: string } | null;

type DetailPanelProps = {
  target: DetailTarget;
  onClose: () => void;
};

const ContractDetails: React.FC<{ contract: Contract }> = ({ contract }) => (
  <div>
    <p className="text-uppercase text-muted small mb-1">{getClientName(contract.clientId)}</p>
    <h2 className="h5">{contract.name}</h2>
    <p className="text-muted">Managed by {contract.manager}</p>

    <dl className="row small">
      <dt className="col-5 text-muted">Contract window</dt>
      <dd className="col-7">{formatDate(contract.startDate)} – {formatDate(contract.endDate)}</dd>
      <dt className="col-5 text-muted">Total value</dt>
      <dd className="col-7">{currency(contract.value)}</dd>
      <dt className="col-5 text-muted">Status</dt>
      <dd className="col-7">{contract.status} · {contract.health}</dd>
    </dl>

    <h3 className="h6 mt-4">Payment schedule</h3>
    <ul className="list-unstyled">
      {contract.paymentTerms.map((term) => (
        <li key={term.label} className="mb-2">
          <div className="fw-semibold">{term.label}</div>
          <small className="text-muted">
            {currency(term.amount)} · {term.milestone} · due {formatDate(term.dueDate)}
          </small>
        </li>
      ))}
    </ul>
  </div>
);

const InvoiceDetails: React.FC<{ invoice: Invoice }> = ({ invoice }) => (
  <div>
    <p className="text-uppercase text-muted small mb-1">Invoice</p>
    <h2 className="h5">{invoice.id}</h2>
    <p className="text-muted">{invoice.clientName}</p>

    <dl className="row small">
      <dt className="col-5 text-muted">Amount</dt>
      <dd className="col-7">{currency(invoice.amount)}</dd>
      <dt className="col-5 text-muted">Due date</dt>
      <dd className="col-7">{formatDate(invoice.dueDate)}</dd>
      <dt className="col-5 text-muted">Status</dt>
      <dd className="col-7">{invoice.status}</dd>
    </dl>

    <h3 className="h6 mt-4">Remarks</h3>
    <p className="text-muted">{invoice.remarks}</p>
  </div>
);

export const DetailPanel: React.FC<DetailPanelProps> = ({ target, onClose }) => {
  if (!target) return null;

  const contract = target.type === 'contract' ? contracts.find((item) => item.id === target.id) : null;
  const invoice = target.type === 'invoice' ? invoices.find((item) => item.id === target.id) : null;

  if ((target.type === 'contract' && !contract) || (target.type === 'invoice' && !invoice)) {
    return null;
  }

  return (
    <div className="detail-panel-overlay" role="dialog" aria-modal="true">
      <div className="detail-panel shadow-lg">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <p className="text-muted mb-0">{target.type === 'contract' ? 'Contract details' : 'Invoice details'}</p>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} type="button">
            Close
          </button>
        </div>
        <div className="detail-panel-body">
          {target.type === 'contract' && contract && <ContractDetails contract={contract} />}
          {target.type === 'invoice' && invoice && <InvoiceDetails invoice={invoice} />}
        </div>
      </div>
    </div>
  );
};
